import { useQuery } from '@tanstack/react-query'
import React from 'react'
import ReportingArtifactCard from 'src/agent/components/ReportingArtifactCard/ReportingArtifactCard'
import type { AskReportingChartQuestion } from 'src/agent/reporting/chartQuestion.types'
import { useTranslate } from 'src/localization/Trns'
import type { LiveReportSummary } from 'src/queries/liveReporting/liveReporting.models'
import { liveReportingQueries } from 'src/queries/liveReporting/liveReporting.queries'

import {
  useVisibleLiveReports,
  type VisibleLiveReportState,
} from '../useVisibleLiveReports'
import * as style from './PinnedReportingSection.style'

export interface PinnedReportingSectionProps {
  actorId?: string
  organizationId?: string
  reportingEnabled?: boolean
  reports: readonly LiveReportSummary[]
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  error?: boolean
  onRetryList?: () => void
  onAskQuestion?: AskReportingChartQuestion
  onAccessDenied?: () => void
}

const PinnedReportSkeleton: React.FC<{ title?: string }> = ({ title }) => (
  <div
    aria-hidden="true"
    css={style.skeletonCard}
    data-testid="pinned-report-skeleton"
  >
    {title ? (
      <h3 css={style.errorHeading}>{title}</h3>
    ) : (
      <span css={style.skeletonHeading} />
    )}
    <span css={style.skeletonBody} />
  </div>
)

const LiveReportStateCard: React.FC<{
  report: LiveReportSummary
  incompatible: boolean
  onRetry: () => void
}> = ({ report, incompatible, onRetry }) => {
  const translate = useTranslate()
  return (
    <div
      css={style.skeletonCard}
      data-testid="live-report-unavailable-card"
      data-reporting-source="live"
      data-report-id={report.id}
      data-reporting-state={incompatible ? 'incompatible' : 'unavailable'}
    >
      <h3 css={style.errorHeading}>{report.title}</h3>
      <p css={style.error} role="status">
        {translate('Pinned reports are unavailable.')}
      </p>
      <button type="button" onClick={onRetry}>
        {translate('Retry')}
      </button>
    </div>
  )
}

const LiveReportsRefreshCard: React.FC<{ onRefresh: () => void }> = ({
  onRefresh,
}) => {
  const translate = useTranslate()
  return (
    <div
      css={style.skeletonCard}
      data-reporting-source="live"
      data-testid="live-report-refresh-card"
    >
      <h3 css={style.errorHeading}>{translate('Pinned reports')}</h3>
      <p css={style.error} role="alert">
        {translate('Pinned reports are unavailable.')}
      </p>
      <button type="button" onClick={onRefresh}>
        {translate('Retry')}
      </button>
    </div>
  )
}

const LiveReportContent: React.FC<{
  actorId?: string
  organizationId?: string
  reportingEnabled: boolean
  report: LiveReportSummary
  state?: VisibleLiveReportState
  loading: boolean
  onRetry: () => void
  onAskQuestion?: AskReportingChartQuestion
}> = ({
  actorId,
  organizationId,
  reportingEnabled,
  report,
  state,
  loading,
  onRetry,
  onAskQuestion,
}) => {
  const translate = useTranslate()
  const resultQuery = useQuery({
    ...liveReportingQueries.result({
      actorId,
      organizationId,
      reportId: report.id,
      reportingEnabled,
    }),
    // The viewport scheduler owns fetching and concurrency. This observer only
    // renders the owner-scoped React Query value, so cache scrubs remove data
    // from the card without leaving a second dataset copy in component state.
    enabled: false,
  })

  if (state?.status === 'success' && resultQuery.data) {
    return (
      <ReportingArtifactCard
        artifact={{
          id: resultQuery.data.report_id,
          title: resultQuery.data.title,
          chartSpec: resultQuery.data.chart_spec,
          dataset: resultQuery.data.dataset,
          sourceContext: {
            kind: 'live_report',
            reportId: resultQuery.data.report_id,
            analysisContextToken: resultQuery.data.analysis_context_token,
          },
        }}
        layout="pinned"
        onAskQuestion={onAskQuestion}
        questionContextLabel={translate('Reporting · Pinned')}
      />
    )
  }
  if (state?.status === 'loading' || loading) {
    return <PinnedReportSkeleton title={report.title} />
  }
  if (state?.status === 'incompatible') {
    return (
      <LiveReportStateCard report={report} incompatible onRetry={onRetry} />
    )
  }
  if (state?.status === 'unavailable') {
    return (
      <LiveReportStateCard
        report={report}
        incompatible={false}
        onRetry={onRetry}
      />
    )
  }
  return null
}

const PinnedReportingSection: React.FC<PinnedReportingSectionProps> = ({
  actorId,
  organizationId,
  reportingEnabled = true,
  reports,
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  error = false,
  onRetryList,
  onAskQuestion,
  onAccessDenied,
}) => {
  const translate = useTranslate()
  const visible = useVisibleLiveReports({
    actorId,
    organizationId,
    reports,
    reportingEnabled,
    onAccessDenied,
  })

  return (
    <section
      aria-label={translate('Pinned reports')}
      css={style.section}
      data-testid="pinned-reporting-section"
    >
      <div
        aria-busy={loading}
        css={style.grid}
        data-testid="pinned-report-grid"
      >
        {error ? (
          <LiveReportsRefreshCard onRefresh={onRetryList ?? (() => {})} />
        ) : null}
        {loading && reports.length === 0 ? <PinnedReportSkeleton /> : null}
        {reports.map((report) => {
          const state = visible.reports.get(report.id)
          return (
            <div
              key={report.id}
              css={style.liveCardSlot}
              ref={visible.register(report.id)}
              data-reporting-source="live"
              data-report-id={report.id}
              data-testid="live-report-slot"
            >
              <LiveReportContent
                actorId={actorId}
                organizationId={organizationId}
                reportingEnabled={reportingEnabled}
                report={report}
                state={state}
                loading={loading}
                onRetry={() => visible.refresh(report.id)}
                onAskQuestion={onAskQuestion}
              />
            </div>
          )
        })}
        {!error && hasMore ? (
          <button type="button" disabled={loadingMore} onClick={onLoadMore}>
            {translate('Load more')}
          </button>
        ) : null}
        {!loading && !error && reports.length === 0 ? (
          <p css={style.empty}>{translate('No pinned reports yet.')}</p>
        ) : null}
      </div>
    </section>
  )
}

export default PinnedReportingSection
